import { isEqual } from 'lodash';
import { useLayoutEffect } from 'react';

import {
  usePageConfig,
  useTabContentConfig,
} from '../components/PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../components/PageConfigProvider/types';
import { usePrimaryFilterState } from '../filters/hooks/usePrimaryFilterState';
import {
  companySentimentSingleSnapshotItems,
  companySentimentSnapshotItems,
} from '../templates/company-sentiment';
import {
  geographySentimentSingleSnapshotItems,
  geographySentimentSnapshotItems,
} from '../templates/geography-sentiment';
import {
  roleSentimentSingleSnapshotItems,
  roleSentimentSnapshotItems,
} from '../templates/role-sentiment';

const isRevelioSentimentPage = (id: string) => {
  return (
    id === RevelioPageId.COMPANY_SENTIMENT ||
    id === RevelioPageId.GEOGRAPHY_SENTIMENT ||
    id === RevelioPageId.ROLE_SENTIMENT
  );
};

const isSentimentSnapshotPage = (id: string) => {
  return (
    id === 'company-sentiment-snapshot' ||
    id === 'geography-sentiment-snapshot' ||
    id === 'role-sentiment-snapshot'
  );
};

const sentimentSingleSnapshotItems = {
  'company-sentiment-snapshot': companySentimentSingleSnapshotItems,
  'geography-sentiment-snapshot': geographySentimentSingleSnapshotItems,
  'role-sentiment-snapshot': roleSentimentSingleSnapshotItems,
};

const sentimentSnapshotItems = {
  'company-sentiment-snapshot': companySentimentSnapshotItems,
  'geography-sentiment-snapshot': geographySentimentSnapshotItems,
  'role-sentiment-snapshot': roleSentimentSnapshotItems,
};

export const useSentimentItemsOverride = () => {
  const { config: pageConfig } = usePageConfig();
  const {
    config,
    dispatch,
    primaryFilter: tabPrimaryFilter,
  } = useTabContentConfig();
  const { primaryFilter } = usePrimaryFilterState(tabPrimaryFilter?.id);

  /** If sentiment snapshot page and a single primary entity selected, set items to single snapshot items */
  useLayoutEffect(() => {
    const singleSnapshotItems =
      sentimentSingleSnapshotItems[
        config.id as keyof typeof sentimentSingleSnapshotItems
      ];
    if (
      isRevelioSentimentPage(pageConfig.id) &&
      isSentimentSnapshotPage(config.id) &&
      primaryFilter?.selections?.length === 1 &&
      !isEqual(config.items, singleSnapshotItems)
    ) {
      dispatch({
        type: 'SET_ITEMS',
        payload: singleSnapshotItems,
      });
    }
  }, [config, primaryFilter?.selections, dispatch, pageConfig.id]);

  /** If sentiment snapshot page and multiple primary entities selected, set items to snapshot items */
  useLayoutEffect(() => {
    const snapshotItems =
      sentimentSnapshotItems[config.id as keyof typeof sentimentSnapshotItems];
    if (
      isRevelioSentimentPage(pageConfig.id) &&
      isSentimentSnapshotPage(config.id) &&
      primaryFilter?.selections &&
      primaryFilter?.selections?.length > 1 &&
      !isEqual(config.items, snapshotItems)
    ) {
      dispatch({
        type: 'SET_ITEMS',
        payload: snapshotItems,
      });
    }
  }, [config, primaryFilter?.selections, dispatch, pageConfig.id]);
};
